import React, { useEffect, useMemo } from 'react';
import { history, useDispatch, useLocation, useParams } from 'umi';
import { Tabs } from 'tea-component';
import HeaderWrap from '../HeaderWrap';
import FullScreenBtn from '../FullScreenBtn';
import StudioPrGamesSidebar from '../StudioPrGamesSidebar';
import { StudioContext } from '../../context';
import { IStudioInfo, IDashboardItem, IStudioHeader } from '../../type';
import {
  STUDIO_OPERATION,
  STUDIO_HUB_SHELL,
  isStudioSidebarGameHubSegment,
} from '@/pages/home/pages/studio/constant';
import { PAGE_TYPE_OUT_PATH, PAGE_TYPE_OUT_URL } from '@/pages/dashboard/_config/constant';
import { getBasePath } from '@/utils/utils';
import {
  buildStudioGamePath,
  findStudioHubMenuByPath,
  getStudioHubBasePath,
  parseStudioHubShellKind,
  resolveRedirectPathForMissingHub,
  getStudioMenuPath
} from '@/pages/home/pages/studio/utils';
import { useStudioHubTabSwitch } from '@/pages/home/pages/studio/hooks/useStudioHubTabSwitch';
import {
  getGameRouteCode,
  resolvePrHubSidebarGames,
} from '@/pages/home/pages/studio/utils/prHubSidebarGames';
import { PR_HUB_TAB_ID_SET } from '@/pages/home/pages/studio/pr/prHubSubTabs';
import { applyPrHubFallbackGameList } from '@/pages/home/pages/studio/pr/prHubFallbackGames';
import CasualHubChrome from '@/pages/home/pages/studio/casualHub/components/CasualHubChrome';
import './prHubWorkspace.scss';

interface IProps {
  children: React.ReactNode;
  studioInfo: IStudioInfo;
};

// studio页面外层组件
const StudioWrap = (props: IProps) => {
  const { studioInfo, children } = props;
  const dispatch = useDispatch();
  const prRouteParams = useParams() as { studioId?: string; };
  const { studioId } = prRouteParams;
  const location = useLocation();
  const studioData = studioInfo?.data as IStudioHeader | undefined;

  const hubShellKind = useMemo(
    () => parseStudioHubShellKind(location.pathname),
    [location.pathname],
  );
  const hubBasePath = useMemo(() => getStudioHubBasePath(hubShellKind), [hubShellKind]);

  /** PR/AI 枢纽：game_list 为空时合并兜底三款游戏，供侧栏、路由与页面内 studioData 一致 */
  const effectiveStudioData = useMemo(() => {
    if (!studioData || !isStudioSidebarGameHubSegment(hubShellKind)) {
      return studioData;
    }
    return applyPrHubFallbackGameList(studioData) ?? studioData;
  }, [studioData, hubShellKind]);

  const prSidebarGames = useMemo(
    () => resolvePrHubSidebarGames(effectiveStudioData),
    [effectiveStudioData],
  );

  const prGamesFiltered = useMemo(
    () => prSidebarGames.filter((g) => Boolean(getGameRouteCode(g))),
    [prSidebarGames],
  );

  /** 路径：侧栏枢纽 /studio/:id/pr|ai、/:gameCode；PR 下旧式误写 Tab 段会重定向（Tab 不记入 URL） */
  const prPathParsed = useMemo(() => {
    if (!isStudioSidebarGameHubSegment(hubShellKind)) {
      return { seg1: undefined as string | undefined, seg2: undefined as string | undefined };
    }
    const m = location.pathname.match(
      new RegExp(
        `^/studio/[^/]+/${hubShellKind}(?:/([^/?#]+))?(?:/([^/?#]+))?`,
      ),
    );
    const seg1 = m?.[1] ? decodeURIComponent(m[1]) : undefined;
    const seg2 = m?.[2] ? decodeURIComponent(m[2]) : undefined;
    return { seg1, seg2 };
  }, [location.pathname, hubShellKind]);

  const prGameCodeFromPath = useMemo(() => {
    if (!isStudioSidebarGameHubSegment(hubShellKind)) {
      return undefined;
    }
    const { seg1, seg2 } = prPathParsed;
    if (!seg1) {
      return undefined;
    }
    if (!seg2 && PR_HUB_TAB_ID_SET.has(seg1)) {
      return undefined;
    }
    return seg1;
  }, [hubShellKind, prPathParsed]);

  const prSelectedGame = useMemo(
    () => prSidebarGames.find((g) => getGameRouteCode(g) === prGameCodeFromPath),
    [prSidebarGames, prGameCodeFromPath],
  );

  const studioDashboardList: IDashboardItem[] =
    studioData?.studio_dashboard_list || [];

  const {
    showTabs,
    tabItems,
    activeTabId,
    onTabChange,
    homeReqParams,
  } = useStudioHubTabSwitch({
    studioDashboardList,
    hubBasePath,
    hubShellKind,
    pathname: location.pathname,
    studioId,
  });

  // hub 路径（/pr、/ai-report 等）未在 studio_dashboard_list 配置时按优先级重定向
  useEffect(() => {
    if (studioId === 'casual' || !studioInfo?.data) return;
    if (hubShellKind === STUDIO_HUB_SHELL.HOME) return;
    const allowed = findStudioHubMenuByPath(
      studioDashboardList,
      getStudioHubBasePath(hubShellKind),
    );
    if (!allowed) {
      const redirectPath = resolveRedirectPathForMissingHub(studioData, studioId!);
      if (redirectPath) {
        history.replace(redirectPath);
      }
    }
  }, [hubShellKind, studioId, studioInfo?.data, studioDashboardList, studioData]);

  // 旧式 /studio/:id/pr/:gameCode/:prTab → 去掉末段（Tab 仅前端状态）
  useEffect(() => {
    if (studioId === 'casual' || hubShellKind !== STUDIO_HUB_SHELL.PR) return;
    const { seg1, seg2 } = prPathParsed;
    if (!seg1 || !seg2) return;
    if (PR_HUB_TAB_ID_SET.has(seg1) || !PR_HUB_TAB_ID_SET.has(seg2)) return;
    history.replace(
      `/studio/${studioId}${hubBasePath}/${encodeURIComponent(seg1)}`,
    );
  }, [hubShellKind, studioId, prPathParsed, hubBasePath]);

  // 历史链接 ?prTab= 清理掉，Tab 不记入 URL
  useEffect(() => {
    if (studioId === 'casual' || hubShellKind !== STUDIO_HUB_SHELL.PR) return;
    const params = new URLSearchParams(location.search);
    if (!params.has('prTab')) return;
    params.delete('prTab');
    const q = params.toString();
    history.replace(q ? `${location.pathname}?${q}` : location.pathname);
  }, [hubShellKind, studioId, location.pathname, location.search]);

  // 侧栏枢纽：补全或修正 URL 中的 :gameCode（与左侧列表一致）
  useEffect(() => {
    if (studioId === 'casual' || !isStudioSidebarGameHubSegment(hubShellKind) || !effectiveStudioData) {
      return;
    }
    const list = resolvePrHubSidebarGames(effectiveStudioData);
    if (!list.length) {
      return;
    }
    const firstCode = getGameRouteCode(list[0]);
    if (!firstCode) {
      return;
    }
    const isBareHub = new RegExp(
      `^/studio/[^/]+/${hubShellKind}/?$`,
    ).test(location.pathname);
    if (isBareHub) {
      history.replace(
        `/studio/${studioId}${hubBasePath}/${encodeURIComponent(firstCode)}`,
      );
      return;
    }
    if (prGameCodeFromPath && !list.some((g) => getGameRouteCode(g) === prGameCodeFromPath)) {
      history.replace(
        `/studio/${studioId}${hubBasePath}/${encodeURIComponent(firstCode)}`,
      );
    }
  }, [hubShellKind, studioId, effectiveStudioData, location.pathname, prGameCodeFromPath, hubBasePath]);

  // /studio/:id/pr/:prTab（误把 Tab 写在 game 位）→ 补全为 /pr/:firstGame（仅 PR 工作台 Tab id）
  useEffect(() => {
    if (studioId === 'casual' || hubShellKind !== STUDIO_HUB_SHELL.PR || !effectiveStudioData) return;
    const list = resolvePrHubSidebarGames(effectiveStudioData);
    const firstCode = list[0] ? getGameRouteCode(list[0]) : '';
    if (!firstCode) return;
    const { seg1 } = prPathParsed;
    if (seg1 && PR_HUB_TAB_ID_SET.has(seg1)) {
      history.replace(
        `/studio/${studioId}${hubBasePath}/${encodeURIComponent(firstCode)}`,
      );
    }
  }, [studioId, hubShellKind, effectiveStudioData, prPathParsed, hubBasePath]);

  useEffect(() => {
    // studio 无studio首页权限，url强制进入studio首页，则返回首页
    if (studioInfo?.data?.studio_info?.studio_code !== studioId) {
      return;
    }
    if (!studioInfo?.data?.haveHome && !studioInfo?.data?.haveSidebarGameHub) {
      // 修复：先检查当前路径是否在 studio_dashboard_list 中有效，避免误重定向
      const currentPath = location.pathname;
      const studioCode = studioInfo?.data?.studio_info?.studio_code;
      const isCurrentPathValid = studioDashboardList.some(item => {
        if (!item.path) return false;
        const itemFullPath = `/studio/${studioCode}${item.path}`;
        return currentPath === itemFullPath;
      });
      if (!isCurrentPathValid) {
        // 当前路径无效，执行原有重定向逻辑
        // 和产品沟通后，当前因为第一个固定是竞品报表，所以不考虑没有报表的情况（在这里做个兜底，如果没有报表则返回首页）
        // 另外和产品沟通后兜底，无论第一个报表是内嵌还是外链，当前都是进入第一个报表（外链的时候打开新页面）
        const firstGame = studioInfo?.data?.game_list?.[0];
        const firstGameCode = firstGame?.game_code || firstGame?.unified_edition_id;
        const firstGameDashboardList = firstGame?.dashboard_list || [];
        const firstDashboard = firstGameDashboardList?.[0];
        const { page_type: pageType, url} = firstDashboard || {};
        // 匹配到路径是外链的情况
        const isOut = pageType === PAGE_TYPE_OUT_PATH || pageType === PAGE_TYPE_OUT_URL;
        // 如果没有报表列表，则返回首页，没有这种情况，有的话说明配置错误
        if (!firstDashboard) {
          history.push('/home');
          return;
        };
        // 如果是外链，则直接跳外链
        if(isOut) {
          window.open(`${getBasePath()}${url}`);
        }
        const findMenuPath = getStudioMenuPath(firstDashboard);
        history.replace(
          buildStudioGamePath(
            studioId,
            firstGameCode,
            findMenuPath,
            STUDIO_OPERATION,
            (firstGame?.dashboard_research_list?.length ?? 0) > 0,
          ),
        );
      }
      // 当前路径有效，跳过重定向，继续执行到 dispatch calls
    }
    // 进入StudioWrap 则代表是工作室类型页面
    dispatch({
      type: 'studio/setFrameType',
      payload: 'studio',
    });

    // 指标注释
    dispatch({
      type: 'studio/getExplainInfo',
      payload: {
        params: {
          studio_code: studioInfo?.data?.studio_info?.studio_code,
          ...(isStudioSidebarGameHubSegment(hubShellKind) && prGameCodeFromPath
            ? { game_code: prGameCodeFromPath }
            : {}),
        },
      },
    });
  }, [studioInfo, hubShellKind, prGameCodeFromPath]);

  const currentGameForContext = useMemo(() => {
    const baseCode = studioData?.studio_info?.studio_code || studioId;
    if (isStudioSidebarGameHubSegment(hubShellKind) && prSelectedGame) {
      return { ...prSelectedGame, studio_code: baseCode };
    }
    return { studio_code: baseCode };
  }, [hubShellKind, prSelectedGame, studioData, studioId]);

  // 为 Home 页面子组件提供 StudioContext（与 GameWrap 对齐）
  const studioContextValue = useMemo(() => ({
    studioInfo: studioData,
    currentGame: currentGameForContext,
    currentMenu: {},
    menuPermission: true,
    editPermission: studioData?.has_studio_editor,
    companyInfo: studioData?.company,
    activeTab: activeTabId,
    activeDashboardList: studioDashboardList,
    onTabChange,
    hasResearchList: false,
  }), [studioData, studioId, currentGameForContext, activeTabId, studioDashboardList, onTabChange]);

  const isCasualStudio = studioId === 'casual';

  return <StudioContext.Provider value={studioContextValue}>
    <HeaderWrap
      studioInfo={studioInfo}
      pageType="studio"
      bgClassName={isCasualStudio ? 'dbh-studio-causal-background' : undefined}
      casualHubBgFallback={isCasualStudio}
      header={
        isCasualStudio
          ? <CasualHubChrome studioInfo={studioInfo} />
          : undefined
      }
      contextClassName={isStudioSidebarGameHubSegment(hubShellKind) ? 'dbh-studio-context--pr-hub' : undefined}
    >
      {showTabs && !isCasualStudio && (
        <div className="dbh-studio-context-tabs">
          <Tabs
            tabs={tabItems}
            activeId={activeTabId}
            onActive={onTabChange}
          />
        </div>
      )}
      {isStudioSidebarGameHubSegment(hubShellKind) ? (
        <div className="dbh-studio-context-body dbh-studio-context-body--pr">
          <StudioPrGamesSidebar
            studioId={studioId ?? ''}
            hubBasePath={hubBasePath}
            games={prGamesFiltered}
            selectedCode={prGameCodeFromPath}
          />
          <div className="dbh-studio-context-right">
            <div className="dbh-studio-context-auto">
              <FullScreenBtn className="dbh-studio-hub-page-fullscreen" priority={0}>
                {React.Children.map(children, (child: any) => {
                  return React.cloneElement(child, {
                    studioData: effectiveStudioData,
                    studioInfo: {
                      ...studioInfo,
                      data: effectiveStudioData,
                    },
                    homeReqParams,
                    prHubGameCode: prGameCodeFromPath,
                  });
                })}
              </FullScreenBtn>
            </div>
          </div>
        </div>
      ) : (
        <div className="dbh-studio-context-right">
          <div className="dbh-studio-context-auto">
            {hubShellKind === STUDIO_HUB_SHELL.AI ? (
              <FullScreenBtn className="dbh-studio-hub-page-fullscreen" priority={0}>
                {React.Children.map(children, (child: any) => {
                  return React.cloneElement(child, {
                    studioData: studioInfo?.data,
                    studioInfo,
                    homeReqParams,
                  });
                })}
              </FullScreenBtn>
            ) : React.Children.map(children, (child: any) => {
              return React.cloneElement(child, {
                studioData: studioInfo?.data,
                studioInfo,
                homeReqParams,
              });
            })}
          </div>
        </div>
      )}
    </HeaderWrap>
  </StudioContext.Provider>;
};

export default StudioWrap;
